/** Copyright (c) 2019 T.Rowe Price. All rights reserved. */

import { cloneDeep } from 'lodash';

import {
  RECEIVED_AVAILABLE_FUNDS,
  RECEIVED_CUSTOMER_PREFERENCES,
  RECEIVED_DIVIDENDS_CAPITAL_GAINS,
  RECEIVED_MUTUAL_FUNDS,
  RECEIVED_DOCUMENT_DELIVERY,
  RECEIVED_UPDATE_CUSTOMER_PREFERENCES,
  RECEIVED_CASCADING_SUBSCRIPTIONS,
  RECEIVED_UPDATE_DOCUMENT_DELIVERY,
  RECEIVED_UPDATE_MUTUAL_FUND_DIVIDENDS_CAPITAL_GAINS,
  HIDE_SHOW_ALL,
  ACCOUNTS_VISIBILITY_UPDATED,
  HIDE_SHOW_CHANGED,
  UPDATE_CUSTOM_ACCOUNT_GROUP,
  SET_CUSTOM_ACCOUNT_GROUP,
  GET_CUSTOM_ACCOUNT_GROUP_DATA,
  ACCOUNT_SELECTION_CHANGE,
  ALL_ACCOUNT_SELECTION_CHANGE,
  REFRESH_CUSTOM_ACCOUNT_GROUPS,
  CHANGE_SELECTED_DEFAULT_START_PAGE,
  CHANGE_SELECTED_DEFAULT_GROUP_ID,
  DISABLE_CONTACT_INFO_SAVE,
  ENABLE_CONTACT_INFO_SAVE,
  HIDE_FIXED_FOOTER_SAVE_OPTIONS,
  SHOW_FIXED_FOOTER_SAVE_OPTIONS,
  UPDATE_DISTRIBUTION_TO_EDIT,
  UPDATE_IS_MAILING_ADDRESS_SAME_AS_RESIDENTIAL
} from 'state/preferences/PreferencesActions';

import {
  RECEIVED_WORKSPACE_PREFERENCES,
  REDIRECTED_TO_DEFAULT_HOME_PAGE,
  RECEIVED_UPDATE_WORKSPACE_PREFERENCES
} from 'state/Actions';

import { documentDeliveryServiceTypeIds } from 'services/Preferences.js';

export const defaultStartPageOptions = [
  {
    label: 'Dashboard',
    value: 'dashboard'
  },
  {
    label: 'Accounts',
    value: 'balanceDetails'
  }
];

export const getActiveCustomAccountGroup = (customerPreferences) => {
  return customerPreferences
    ? customerPreferences.find((x) => !x.pristine)
    : {};
};

export const contactInfoSaveButtonReducer = (state, action) => {
  if (action.type === DISABLE_CONTACT_INFO_SAVE) {
    return true;
  }

  if (action.type === ENABLE_CONTACT_INFO_SAVE) {
    return false;
  }

  return state;
};

export const fixedFooterSaveOptionsReducer = (state, action) => {
  if (action.type == HIDE_FIXED_FOOTER_SAVE_OPTIONS) {
    return true;
  }

  if (action.type == SHOW_FIXED_FOOTER_SAVE_OPTIONS) {
    return false;
  }

  return state;
};

const stateWithCustomAccountGroups = (state, data) => {
  const { customAccountGroups } = state;
  const updatedCustomAccountGroups = customAccountGroups.map((group) => {
    if (group.pristine) {
      return group;
    }

    return {
      ...group,
      ...data,
      pristine: false,
      updated: true
    };
  });
  return {
    ...state,
    customAccountGroups: updatedCustomAccountGroups
  };
};

export const customerPreferencesInitialState = {
  contactInformation: {},
  trustedContacts: [],
  beneficiaries: [],
  defaultGroupBy: {
    preferredStartPage: 'dashboard',
    hasRedirectedToDefault: false
  },
  customAccountGroups: [],
  accountGroupingId: []
};

export const trustedContactInitialState = {
  emailAddress: {
    address: ''
  },
  firstName: '',
  lastName: '',
  mailingAddress: {
    addressOne: '',
    addressTwo: '',
    city: '',
    state: '',
    zipCode: '',
    zipCodeExt: ''
  },
  phoneNumber: {},
  relationship: ''
};

export const accountVisibilitySaveButtonReducer = (state = true, action) => {
  if (action.type === ACCOUNTS_VISIBILITY_UPDATED) {
    return true;
  }

  if (action.type === HIDE_SHOW_ALL) {
    if (!action.hideOrShow) {
      return true;
    }
    return false;
  }

  if (action.type === HIDE_SHOW_CHANGED) {
    return false;
  }

  return state;
};

export const distributionToEditReducer = (
  state = { distribution: {} },
  action
) => {
  const { type } = action;

  if (type === UPDATE_DISTRIBUTION_TO_EDIT) {
    const { distribution } = action;
    return distribution;
  }

  return state;
};

export const customerPreferencesReducer = (
  state = customerPreferencesInitialState,
  action
) => {
  const { type } = action;

  if (type === UPDATE_CUSTOM_ACCOUNT_GROUP) {
    const { field, value } = action;
    return stateWithCustomAccountGroups(state, { [field]: value });
  }

  if (type === SET_CUSTOM_ACCOUNT_GROUP) {
    const { groupId } = action;
    const { customAccountGroups } = state;
    const defaultCustomAccountGroup = {
      groupId: null,
      groupName: '',
      accountDescriptors: [],
      disposition: null,
      pristine: false
    };

    if (!groupId) {
      return {
        ...state,
        customAccountGroups: [...customAccountGroups, defaultCustomAccountGroup]
      };
    }

    const selectedCustomAccountGroup = customAccountGroups.find(
      (x) => x.groupId === groupId
    );
    const pristineCustomAccountGroup = JSON.parse(
      JSON.stringify(selectedCustomAccountGroup)
    );
    return {
      ...state,
      customAccountGroups: [
        ...customAccountGroups,
        { ...pristineCustomAccountGroup, pristine: false }
      ]
    };
  }

  if (type === REFRESH_CUSTOM_ACCOUNT_GROUPS) {
    const { customAccountGroups } = state;
    const refreshCustomAccountGroups = customAccountGroups.filter(
      (x) => x.pristine
    );
    return {
      ...state,
      customAccountGroups: refreshCustomAccountGroups
    };
  }

  if (type === ACCOUNT_SELECTION_CHANGE) {
    const { accountDescriptor, group } = action;
    const { customAccountGroups } = state;
    let isGroupSelection = false;
    if (group && group.children) {
      isGroupSelection = !!group.children;
    }

    const activeCustomAccountGroup =
      getActiveCustomAccountGroup(customAccountGroups);
    let selectedAccountsInGroup = 0;
    if (group && group.children) {
      group.children.forEach((account) => {
        selectedAccountsInGroup = account.details.selected
          ? selectedAccountsInGroup + 1
          : selectedAccountsInGroup;
      });
    }
    let accountDescriptors = activeCustomAccountGroup.accountDescriptors;
    const accountIndex = accountDescriptors.findIndex(
      (x) => x.accountId === accountDescriptor.accountId
    );

    if (accountIndex > -1) {
      if (isGroupSelection) {
        if (selectedAccountsInGroup === group.children.length) {
          accountDescriptors = accountDescriptors.filter(
            (x) => x.accountId !== accountDescriptor.accountId
          );
        } else {
          accountDescriptors = [...accountDescriptors];
        }
      } else {
        accountDescriptors = accountDescriptors.filter(
          (x) => x.accountId !== accountDescriptor.accountId
        );
      }
    } else {
      accountDescriptors = [...accountDescriptors, accountDescriptor];
    }

    return stateWithCustomAccountGroups(state, { accountDescriptors });
  }

  if (type === ALL_ACCOUNT_SELECTION_CHANGE) {
    const { accountDescriptors } = action;
    return stateWithCustomAccountGroups(state, { accountDescriptors });
  }

  if (type === RECEIVED_CUSTOMER_PREFERENCES) {
    const preferenceKeys = Object.keys(state);
    for (const key of preferenceKeys) {
      if (action.preferences[key]) {
        if (key === 'customAccountGroups') {
          action.preferences[key].forEach((x) => (x.pristine = true));
        }
        if (key === 'defaultGroupBy') {
          action.preferences[key] = {
            ...state.defaultGroupBy,
            ...action.preferences[key]
          };
        }
      }
    }
    return action.preferences;
  }

  if (
    type === RECEIVED_UPDATE_CUSTOMER_PREFERENCES ||
    type === RECEIVED_WORKSPACE_PREFERENCES ||
    type === RECEIVED_UPDATE_WORKSPACE_PREFERENCES
  ) {
    const preferenceKeys = Object.keys(state);

    for (const key of preferenceKeys) {
      if (action.preferences[key]) {
        if (key === 'contactInformation') {
          const contactInformation = action.preferences[key];
          return {
            ...state,
            [key]: cleanContactInformation(
              contactInformation,
              state.contactInformation
            )
          };
        }

        if (key === 'trustedContacts') {
          const trustedContacts = action.preferences[key];
          return {
            ...state,
            [key]: cleanTrustedContacts(trustedContacts)
          };
        }

        if (key === 'beneficiaries') {
          const beneficiaries = action.preferences[key];
          return {
            ...state,
            [key]: cleanBeneficiaries(beneficiaries)
          };
        }

        if (key === 'defaultGroupBy') {
          return {
            ...state,
            [key]: action.preferences[key]
          };
        }

        if (key === 'customAccountGroups') {
          const customAccountGroups = action.preferences[key].map((x) => {
            x.pristine = true;
            return x;
          });
          return {
            ...state,
            customAccountGroups
          };
        }
      }
    }
  }

  if (type === REDIRECTED_TO_DEFAULT_HOME_PAGE) {
    const defaultGroupBy = state.defaultGroupBy;
    defaultGroupBy.hasRedirectedToDefault = true;
    return {
      ...state,
      defaultGroupBy
    };
  }

  if (type === UPDATE_IS_MAILING_ADDRESS_SAME_AS_RESIDENTIAL) {
    return {
      ...state,
      contactInformation: {
        ...state.contactInformation,
        primaryMailingAddress: {
          ...state.contactInformation.primaryMailingAddress,
          sameAsResidential: action.isChecked
        }
      }
    };
  }

  if (type === RECEIVED_UPDATE_DOCUMENT_DELIVERY) {
    const { subscriptions } = state;
    const existingSubscriptions = cloneDeep(subscriptions);
    const updatedSubscriptions = action.subscriptions;
    const { complianceSubscriptions: existingCompliance } =
      existingSubscriptions;
    const { complianceSubscriptions: updatedCompliance } = updatedSubscriptions;
    if (updatedCompliance && existingCompliance) {
      return {
        ...state,
        subscriptions: updateExistingComplianceSubscriptions(
          existingSubscriptions,
          updatedCompliance
        )
      };
    }
    return state;
  }

  return state;
};

export const selectedDefaultViewSettingsReducer = (state, action) => {
  switch (action.type) {
    case CHANGE_SELECTED_DEFAULT_START_PAGE:
      return {
        ...state,
        selectedDefaultStartPage: action.selection
      };

    case CHANGE_SELECTED_DEFAULT_GROUP_ID:
      return {
        ...state,
        selectedDefaultGroupId: action.selection
      };

    case GET_CUSTOM_ACCOUNT_GROUP_DATA:
      return {
        ...state,
        customAccountGroups: action.selection
      };
    case RECEIVED_UPDATE_CUSTOMER_PREFERENCES: {
      if (action.preferences && action.preferences.customAccountGroups) {
        const customAccountGroups = action.preferences[
          'customAccountGroups'
        ].map((x) => {
          x.pristine = true;
          return x;
        });
        return {
          ...state,
          customAccountGroups
        };
      }
      return state;
    }
    case RECEIVED_CUSTOMER_PREFERENCES:
    case RECEIVED_WORKSPACE_PREFERENCES: {
      const { accountGroupingId, preferredStartPage } =
        action.preferences.defaultGroupBy;
      const { customAccountGroups } = action.preferences;
      return {
        selectedDefaultStartPage: preferredStartPage
          ? preferredStartPage
          : 'dashboard',
        selectedDefaultGroupId: accountGroupingId ? accountGroupingId : 20,
        customAccountGroups: customAccountGroups ? customAccountGroups : []
      };
    }
    default:
      return state;
  }
};

export const updateShowAllReducer = (state = [], action) => {
  if (action.type === HIDE_SHOW_ALL) {
    return action.hideOrShow;
  }
  return state;
};

const updateExistingComplianceSubscriptions = (
  existingSubscriptions,
  updatedCompliance
) => {
  const { complianceSubscriptions: existingCompliance } = existingSubscriptions;
  updatedCompliance.forEach((sub) => {
    const existingIndex = existingCompliance.findIndex(
      (subscription) => sub.subscriptionId === subscription.subscriptionId
    );
    if (existingIndex > -1) {
      const updatedIndex = updatedCompliance.findIndex(
        (sub) =>
          sub.subscriptionId ===
          existingCompliance[existingIndex].subscriptionId
      );
      if (updatedIndex > -1) {
        if (!updatedCompliance[updatedIndex].subscribed) {
          existingCompliance.splice(existingIndex, 1);
        } else {
          existingCompliance[existingIndex].subscribed =
            updatedCompliance[updatedIndex].subscribed;
        }
      }
    } else {
      existingCompliance.push(sub);
    }
  });

  return existingSubscriptions;
};

const cleanBeneficiaries = (beneficiaries) =>
  beneficiaries
    .filter((currentBeneficiary) => {
      const { disposition } = currentBeneficiary;
      return !disposition || disposition.action !== 'remove';
    })
    .map((currentBeneficiary) => ({
      ...currentBeneficiary,
      disposition: null
    }));

const cleanTrustedContacts = (trustedContacts) =>
  trustedContacts.map((contact) => ({
    ...contact,
    emailAddress: { ...contact.emailAddress, disposition: null },
    mailingAddress: { ...contact.mailingAddress, disposition: null },
    phoneNumber: { ...contact.phoneNumber, disposition: null },
    disposition: null
  }));

const cleanContactInformation = (
  contactInformation,
  previousContactInformation = {}
) => {
  const cleanedContactInformation = {};

  if (contactInformation.residentialAddress) {
    cleanedContactInformation.residentialAddress = {
      ...contactInformation.residentialAddress,
      disposition: null
    };

    cleanedContactInformation.mailingAddress = {
      ...contactInformation.mailingAddress,
      disposition: null
    };
  }

  if (contactInformation.primaryMailingAddress) {
    cleanedContactInformation.primaryMailingAddress = {
      ...contactInformation.primaryMailingAddress,
      disposition: null
    };
  }
  cleanedContactInformation.primaryEmailAddress = {
    ...contactInformation.primaryEmailAddress,
    disposition: null
  };
  cleanedContactInformation.previousEmailAddress = {
    ...previousContactInformation.primaryEmailAddress
  };
  const cleanedPhoneNumbers = [];

  contactInformation.phoneNumbers.forEach((phoneNumber) => {
    cleanedPhoneNumbers.push({
      ...phoneNumber,
      disposition: null
    });
  });

  cleanedContactInformation.phoneNumbers = cleanedPhoneNumbers;

  return cleanedContactInformation;
};

export const dividendsCapitalGainsInitialState = {
  distributionOptions: [],
  withholdingOptions: [],
  bankAccounts: []
};

export const dividendsCapitalGainsReducer = (
  state = dividendsCapitalGainsInitialState,
  action
) => {
  if (action.type === RECEIVED_DIVIDENDS_CAPITAL_GAINS) {
    return action.dividendsAndCapitalGains;
  }

  if (action.type === RECEIVED_UPDATE_MUTUAL_FUND_DIVIDENDS_CAPITAL_GAINS) {
    const { dividendsAndCapitalGains } = action;
    const { distributionOptions, withholdingOptions } =
      dividendsAndCapitalGains;

    const cleanDistributions = cleanOptions(distributionOptions);
    const cleanWithholdings = cleanOptions(withholdingOptions);

    return {
      ...state,
      distributionOptions: [...cleanDistributions],
      withholdingOptions: [...cleanWithholdings]
    };
  }

  return state;
};

const cleanOptions = (options) => {
  return options
    .filter((option) => {
      const { disposition } = option;
      return !disposition || disposition.action !== 'remove';
    })
    .map((option) => ({
      ...option,
      disposition: null
    }));
};

export const availableFundsInitialState = [];
export const availableFundsReducer = (
  state = availableFundsInitialState,
  action
) => {
  if (action.type === RECEIVED_AVAILABLE_FUNDS) {
    return action.availableFunds;
  }

  return state;
};

export const mutualFundsInitialState = [];
export const mutualFundsReducer = (state = mutualFundsInitialState, action) => {
  if (action.type === RECEIVED_MUTUAL_FUNDS) {
    return action.mutualFunds;
  }

  return state;
};

/*
TODO: These are currently commented out because they were impacting compliance
subscripitions updates when they should have had no bearing. But leaving here
for reference so that the developer who picks up subscriptions better understands
the complex state shared between doc delivery and subscriptions. If unused
in refactor these 2 functions can be removed from the code.

const findMatchingSubscription = (subscriptions, targetId) => {
  subscriptions.find(sub => sub.subscriptionId === targetId);
};
const isComplianceSubscriptions = key => key === 'complianceSubscriptions';


const mergeSubscriptionArrays = (existingSubs, newSubs, key) => {
  if (isComplianceSubscriptions(key)) {
    return existingSubs
      .map(sub => findMatchingSubscription(newSubs, sub.subscriptionId) || sub)
      .concat(
        newSubs.filter(
          newSub =>
            !findMatchingSubscription(existingSubs, newSub.subscriptionId)
        )
      );
  }
}
*/

export const documentDeliveryInitialState = {
  complianceSubscriptions: documentDeliveryServiceTypeIds.map((id) => ({
    subscriptionId: id,
    subscribed: false
  }))
};

export const documentDeliveryReducer = (
  state = documentDeliveryInitialState,
  action
) => {
  if (
    action.type === RECEIVED_CASCADING_SUBSCRIPTIONS ||
    action.type === RECEIVED_UPDATE_DOCUMENT_DELIVERY
  ) {
    const existingSubscriptions = cloneDeep(state);
    const updatedSubscriptions = action.subscriptions;
    const { complianceSubscriptions: existingCompliance } =
      existingSubscriptions;
    const { complianceSubscriptions: updatedCompliance } = updatedSubscriptions;
    if (updatedCompliance && existingCompliance) {
      return updateExistingComplianceSubscriptions(
        existingSubscriptions,
        updatedCompliance
      );
    }
    return state;
  }

  return action.type === RECEIVED_DOCUMENT_DELIVERY
    ? { ...state, ...action.subscriptions }
    : state;
};
